import { motion } from "framer-motion";
import { BRAND, waLink } from "@/lib/brand";
import { FaWhatsapp } from "react-icons/fa";
import { Phone, MapPin, Clock } from "lucide-react";
import { useT } from "@/lib/i18n";

export default function Contact() {
  const { t } = useT();
  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36 bg-[#0B1733] overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[600px] border border-gold/10 rounded-full pointer-events-none" />
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-[400px] w-[400px] border border-gold/15 rounded-full pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-xs tracking-[0.3em] text-gold uppercase mb-6">{t("ctc.eyebrow")}</div>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] mb-8">
            {t("ctc.title.1")}{" "}
            <span className="italic text-gold gold-shine">{t("ctc.title.2")}</span>
          </h2>
          <p className="text-slate-300/90 text-lg max-w-2xl mx-auto leading-relaxed mb-12">{t("ctc.blurb")}</p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a
              href={waLink(t("wa.default"))}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-cta"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-navy-900 font-semibold px-9 py-4 rounded-full transition-[color,background-color,border-color,box-shadow,transform] shadow-[0_15px_50px_-10px_rgba(201,162,75,0.6)] hover:-translate-y-1"
            >
              <FaWhatsapp className="h-5 w-5" />
              {t("ctc.cta_wa")}
            </a>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="contact-phone-cta"
              className="inline-flex items-center gap-3 border border-gold/50 hover:border-gold hover:bg-gold/5 text-gold font-medium px-9 py-4 rounded-full transition-[color,background-color,border-color,box-shadow,transform]"
            >
              <Phone className="h-4 w-4" />
              {t("ctc.cta_call", { phone: BRAND.phone })}
            </a>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <InfoCard icon={Phone} label={t("ctc.phone")} value={BRAND.phone} />
            <InfoCard icon={MapPin} label={t("ctc.based")} value={t("ctc.based_val")} />
            <InfoCard icon={Clock} label={t("ctc.reply")} value={t("ctc.reply_val")} />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-navy-800/60 border border-gold/20 rounded-2xl p-6 text-left">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gold/10 border border-gold/30 text-gold mb-4">
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-xs tracking-widest text-gold uppercase mb-1">{label}</div>
      <div className="text-white font-medium">{value}</div>
    </div>
  );
}
